<template>
  <div class="subs-redesign">
    <h1 class="sr-title">{{ title }}</h1>
    <p class="sr-sub">{{ subtitle }}</p>

    <!-- Banner da campanha e cards de preço (TEL-2465); quem monta é o pai -->
    <slot name="showcase"></slot>

    <!-- Faixa "Como funciona" (informativa, não clicável) -->
    <div class="howit">
      <span class="howit-eyebrow">Como funciona</span>
      <span class="howit-step">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z"></path><path d="M13 5v2"></path><path d="M13 11v2"></path><path d="M13 17v2"></path></svg>
        {{ licensesEnabled ? 'Um plano por licença' : 'Um plano por usuário' }}
      </span>
      <span class="howit-sep">·</span>
      <span class="howit-step">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><circle cx="12" cy="12" r="6"></circle><circle cx="12" cy="12" r="2"></circle></svg>
        Escolha pelo perfil de uso
      </span>
      <span class="howit-sep">·</span>
      <span class="howit-step">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="20" height="12" x="2" y="6" rx="2"></rect><circle cx="12" cy="12" r="2"></circle><path d="M6 12h.01M18 12h.01"></path></svg>
        Valor fixo por mês
      </span>
    </div>
    <p v-if="audience === 'migration'" class="howit-note">
      Você está saindo dos créditos pré-pagos para planos mensais por usuário — sem recarga e com valor previsível.
    </p>

    <div class="layout">
      <div class="col-main">
        <!-- Plano de cada usuário -->
        <div ref="usersPanel" class="panel">
          <div class="panel-head">
            <h2>Plano de cada usuário</h2>
            <div class="head-actions">
              <button
                v-if="showSuggestionReset"
                type="button"
                class="resugg dirty"
                title="Refaz a sugestão de plano de cada usuário com base no consumo do mês"
                @click="$emit('reset-suggestions')">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z"></path><path d="M20 3v4"></path><path d="M22 5h-4"></path><path d="M4 17v2"></path><path d="M5 18H3"></path></svg>
                Sugestão inteligente
              </button>
              <select
                v-if="showMonthFilter"
                class="monthsel"
                title="Mês usado como base do consumo: troque para ver os minutos daquele período e recalcular a sugestão de plano."
                :value="selectedMonth"
                :disabled="isLoading"
                @change="$emit('change-month', $event.target.value)">
                <option v-for="option in monthOptions" :key="option.value" :value="option.value">{{ option.text }}</option>
              </select>
            </div>
          </div>
          <p v-if="showListHint" class="hint">{{ copy.listHint }}</p>

          <div v-if="users.length > 1" class="bulk">
            <span class="lbl">Aplicar a todos:</span>
            <span class="planpick">
              <select
                class="psel"
                :class="'psel--' + (bulkPlan || 'none')"
                :value="bulkPlan || 'none'"
                @change="applyAll($event.target.value)">
                <option value="none">Selecione…</option>
                <option v-for="plan in plansByIa4comThenPrice" :key="plan.code" :value="plan.code">{{ plan.name }}</option>
              </select>
            </span>
          </div>

          <div class="table-wrap">
            <div v-if="isLoading" class="table-loading">Carregando dados...</div>
            <table class="people">
              <thead>
                <tr>
                  <th>Usuário</th>
                  <th class="c">Consumo</th>
                  <th class="c">Plano</th>
                  <th class="r">Mensalidade</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="user in users" :key="user.key">
                  <td>
                    <div class="uname" :title="user.name">{{ user.name }}</div>
                    <div class="umail" :title="user.email">{{ user.email }}</div>
                  </td>
                  <td class="c consumo" :class="{ over: isOverLimit(user) }">
                    <span :title="isOverLimit(user) ? 'Consumo acima do limite — sugerimos um plano com mais minutos.' : null">{{ user.minutes }} min</span>
                  </td>
                  <td class="c">
                    <span class="planpick">
                      <select
                        class="psel"
                        :class="'psel--' + planKey(user)"
                        :value="planKey(user)"
                        @change="pick(user, $event.target.value)">
                        <option value="none">Selecione…</option>
                        <option v-for="plan in plansByIa4comThenPrice" :key="plan.code" :value="plan.code">{{ plan.name }}</option>
                      </select>
                    </span>
                  </td>
                  <td class="r">
                    <span class="fee" :class="{ empty: !selectedPlanOf(user) }">{{ feeLabel(user) }}</span>
                  </td>
                </tr>
              </tbody>
              <tfoot>
                <tr>
                  <td>TOTAL</td>
                  <td class="c">{{ totalMinutes }} min</td>
                  <td class="c">{{ assignedCount }} de {{ users.length }} com plano</td>
                  <td class="r">{{ money(peopleFee) }}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        <!-- Licenças adicionais — desligado nesta entrega; quem liga é a TEL-2147 -->
        <div v-if="licensesEnabled" class="panel panel--lic" :class="{ spotlight: isSingleUserAccount }">
          <div class="panel-head">
            <h2>Licenças adicionais <span class="tag-opt">opcional</span></h2>
          </div>
          <p class="hint">{{ copy.licenseHint }}</p>
          <div class="lichelp">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18h6"></path><path d="M10 22h4"></path><path d="M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0 0 18 8 6 6 0 0 0 6 8c0 1 .23 2.23 1.5 3.5A4.61 4.61 0 0 1 8.91 14"></path></svg>
            <span>Uma <b>licença</b> é um acesso contratado que ainda não tem usuário vinculado. Ela já entra nesta cobrança e fica <b>livre</b> em Usuários › Licenças até você convidar alguém — sem cobrança nova nesse momento.</span>
          </div>

          <div v-for="plan in licensablePlans" :key="plan.code" class="licrow">
            <div class="licname">
              <span class="dot" :class="'dot--' + plan.code"></span>{{ plan.name }}
              <span class="licunit">{{ money(plan.monthlyFee) }} /mês cada</span>
            </div>
            <div class="stepper">
              <button
                type="button"
                aria-label="remover licença"
                :disabled="licenseQty(plan.code) === 0"
                @click="bumpLicense(plan.code, -1)">−</button>
              <span class="qty">{{ licenseQty(plan.code) }}</span>
              <button
                type="button"
                aria-label="adicionar licença"
                :disabled="licenseQty(plan.code) >= maxLicensesPerPlan"
                @click="bumpLicense(plan.code, 1)">+</button>
            </div>
            <div class="licsub" :class="{ empty: licenseQty(plan.code) === 0 }">
              {{ licenseQty(plan.code) === 0 ? '—' : money(licenseQty(plan.code) * plan.monthlyFee) }}
            </div>
          </div>

          <div class="licfoot">
            <span>{{ totalLicenses === 0 ? 'Nenhuma licença reservada' : plural(totalLicenses, 'licença reservada', 'licenças reservadas') }}</span>
            <span class="v">{{ money(licenseFee) }}</span>
          </div>
        </div>

        <slot name="extras"></slot>
      </div>

      <!-- Resumo (sticky — sempre visível durante a rolagem) -->
      <aside class="summary">
        <h3>Resumo da Cobrança</h3>
        <div v-if="summaryRows.length === 0" class="srow">
          <div class="l1"><span>Nenhum plano selecionado</span><span>—</span></div>
        </div>
        <div v-for="row in summaryRows" :key="row.code" class="srow">
          <div class="l1">
            <span>{{ row.name }}</span>
            <span>{{ row.value === 0 ? 'Grátis' : money(row.value) }}</span>
          </div>
          <div class="l2">
            <span v-if="row.users > 0">{{ plural(row.users, 'usuário', 'usuários') }}</span>
            <span v-if="row.users > 0 && row.licenses > 0"> + </span>
            <span v-if="row.licenses > 0" class="lic">{{ plural(row.licenses, 'licença', 'licenças') }}</span>
          </div>
        </div>

        <div class="stotal">
          <span>Total/mês</span>
          <span class="v">{{ canProceed ? money(total) : '—' }}</span>
        </div>

        <div class="due">
          <div class="lbl">📅 Vencimento desta cobrança</div>
          <div class="big">{{ dueDate || '—' }}</div>
          <div class="lbl next">Próximas cobranças: dia {{ billingDay }} de cada mês</div>
        </div>

        <p v-if="gateMessage" class="gate">{{ gateMessage }}</p>
        <button
          type="button"
          class="cta"
          :disabled="!canProceed || isSubmitting"
          @click="submit">
          {{ isSubmitting ? 'Aguarde...' : 'Continuar para pagamento' }}
        </button>
        <p class="trust">🔒 Pagamento seguro</p>
      </aside>
    </div>
  </div>
</template>

<script>
import { formatCurrency } from '../../utils/currency'

const NONE = 'none'
const MAX_LICENSES_PER_PLAN = 99
const LICENSE_HINT = 'Vai contratar para quem ainda não está na conta? Reserve licenças e convide depois, sem nova cobrança.'
// Título do Figma (TEL-2465), o mesmo para conta nova e para conta em migração: quem
// migra continua avisado pela dica da lista ("Com base no consumo do mês...").
const SCREEN_TITLE = 'Planos e assinatura'
const SCREEN_SUBTITLE = 'Escolha o plano de cada pessoa da equipe. Valor fixo por mês, sem recarga.'

// Com IA4COM primeiro, cada grupo do mais caro ao mais barato. Ordena uma cópia.
function sortByIa4comThenPrice (plans) {
  return plans.slice().sort((first, second) => {
    const firstHasIa4com = first.hasIa4com === true
    if (firstHasIa4com !== (second.hasIa4com === true)) return firstHasIa4com ? -1 : 1
    return Number(second.monthlyFee) - Number(first.monthlyFee)
  })
}

export default {
  name: 'PricingRedesign',
  props: {
    // [{ code, name, monthlyFee, limitMinutes, hasIa4com, originalPrice, features }]
    // limitMinutes: 0 não liga, null ilimitado · originalPrice: preço riscado ou null
    plans: {
      type: Array,
      default: () => []
    },
    // [{ key, name, email, minutes, suggestedPlan }]
    users: {
      type: Array,
      default: () => []
    },
    // Mapa controlado pelo pai: user.key -> planCode
    selectedPlans: {
      type: Object,
      default: () => ({})
    },
    // A conta já pagou alguma cobrança — mesma definição que o comercial usa
    // para separar MIGRAÇÃO de NOVO PLANO no deal.
    isMigration: {
      type: Boolean,
      default: false
    },
    // Houve consumo no mês selecionado. Não esconde nada da tela: a coluna
    // Consumo e o filtro de mês são fixos. Serve só para o botão de refazer a
    // sugestão, que sem consumo não teria em que se basear.
    hasUsageHistory: {
      type: Boolean,
      default: false
    },
    monthOptions: {
      type: Array,
      default: () => []
    },
    selectedMonth: {
      type: String,
      default: null
    },
    // Bloco de licenças: a interface fica pronta porém desligada. Com `false`
    // nada de licença chega à tela nem ao payload — quem liga é a TEL-2147,
    // junto do backend (PRD TEL-2147, seção 10).
    licensesEnabled: {
      type: Boolean,
      default: false
    },
    dueDate: {
      type: String,
      default: ''
    },
    billingDay: {
      type: Number,
      default: 10
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    isSubmitting: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      title: SCREEN_TITLE,
      subtitle: SCREEN_SUBTITLE,
      licenses: {},
      bulkPlan: null,
      maxLicensesPerPlan: MAX_LICENSES_PER_PLAN
    }
  },
  computed: {
    // Só existem dois textos, e a chave é uma pergunta só: a conta já pagou
    // alguma cobrança? Reativação lê como migração; conta enxuta cai num dos
    // dois conforme já tenha pago ou não. Menos variação para o time interno
    // ter que decifrar, e menos cópia para manter.
    audience () {
      return this.isMigration ? 'migration' : 'new'
    },
    copy () {
      if (this.audience === 'migration') {
        return {
          listHint: 'Com base no consumo do mês selecionado — ajuste se precisar.',
          licenseHint: LICENSE_HINT
        }
      }
      return {
        listHint: 'Use ‘Aplicar a todos’ para agilizar, ou selecione um a um.',
        licenseHint: LICENSE_HINT
      }
    },
    // Com uma pessoa só não existe "aplicar a todos": a dica sumiria com o
    // controle que ela descreve, em vez de virar um texto próprio.
    showListHint () {
      return this.audience === 'migration' || this.users.length > 1
    },
    // Conta com uma pessoa só é a que mais precisa das licenças — o destaque é
    // visual, não muda texto.
    isSingleUserAccount () {
      return this.users.length <= 1
    },
    // O filtro é a única saída de um mês sem ligações: amarrá-lo ao consumo
    // prendia a conta no mês vazio, sem como voltar. Só some se o pai não
    // mandar mês nenhum.
    showMonthFilter () {
      return this.monthOptions.length > 0
    },
    // Mesma ordem da compra de licenças. Vale para o plano de cada usuário e para as
    // licenças adicionais.
    plansByIa4comThenPrice () {
      return sortByIa4comThenPrice(this.plans)
    },
    // Gerencial fica fora do seletor de licenças: é grátis e o convite já é
    // imediato e sem cobrança — não há o que reservar (PRD 5.1).
    licensablePlans () {
      return this.plansByIa4comThenPrice.filter(plan => plan.monthlyFee > 0)
    },
    showSuggestionReset () {
      if (!this.hasUsageHistory) return false
      return this.users.some(user => this.planKey(user) !== (user.suggestedPlan || NONE))
    },
    assignedCount () {
      return this.users.filter(user => !!this.selectedPlanOf(user)).length
    },
    totalMinutes () {
      return this.users.reduce((acc, user) => acc + Number(user.minutes || 0), 0)
    },
    peopleFee () {
      return this.users.reduce((acc, user) => {
        const plan = this.selectedPlanOf(user)
        return acc + (plan ? plan.monthlyFee : 0)
      }, 0)
    },
    totalLicenses () {
      if (!this.licensesEnabled) return 0
      return this.licensablePlans.reduce((acc, plan) => acc + this.licenseQty(plan.code), 0)
    },
    licenseFee () {
      if (!this.licensesEnabled) return 0
      return this.licensablePlans.reduce((acc, plan) => acc + this.licenseQty(plan.code) * plan.monthlyFee, 0)
    },
    total () {
      return this.peopleFee + this.licenseFee
    },
    summaryRows () {
      return this.plans.map(plan => {
        const userCount = this.users.filter(user => this.planKey(user) === plan.code).length
        const licenseCount = this.licenseQty(plan.code)
        return {
          code: plan.code,
          name: plan.name,
          users: userCount,
          licenses: licenseCount,
          value: (userCount + licenseCount) * plan.monthlyFee
        }
      }).filter(row => row.users > 0 || row.licenses > 0)
    },
    // O único gate é todo mundo ter plano. Cobrança zerada (equipe inteira em
    // Gerencial) segue válida e cai no fluxo de hoje: a modal avisando que o
    // time comercial entra em contato.
    canProceed () {
      return this.users.length > 0 && this.assignedCount === this.users.length
    },
    isFreeSelection () {
      return this.canProceed && this.total <= 0
    },
    gateMessage () {
      // Lista ainda carregando não é lista vazia.
      if (this.isLoading) return ''
      if (this.users.length === 0) return 'Nenhum usuário ativo encontrado na conta.'
      if (this.assignedCount !== this.users.length) return 'Escolha o plano de todas as pessoas para continuar.'
      return ''
    }
  },
  watch: {
    plans: {
      immediate: true,
      handler () {
        const licenses = {}
        this.licensablePlans.forEach(plan => {
          licenses[plan.code] = this.licenses[plan.code] || 0
        })
        this.licenses = licenses
      }
    }
  },
  methods: {
    money (value) {
      return formatCurrency(value)
    },
    plural (count, singular, pluralWord) {
      return count + ' ' + (count === 1 ? singular : pluralWord)
    },
    planByCode (code) {
      return this.plans.find(plan => plan.code === code) || null
    },
    planKey (user) {
      const code = this.selectedPlans[user.key]
      return this.planByCode(code) ? code : NONE
    },
    selectedPlanOf (user) {
      return this.planByCode(this.selectedPlans[user.key])
    },
    feeLabel (user) {
      const plan = this.selectedPlanOf(user)
      if (!plan) return '—'
      return plan.monthlyFee === 0 ? 'Grátis' : this.money(plan.monthlyFee)
    },
    isOverLimit (user) {
      const plan = this.selectedPlanOf(user)
      if (!plan || plan.limitMinutes === null || plan.limitMinutes === undefined) return false
      return Number(user.minutes || 0) > plan.limitMinutes
    },
    // Zera na origem quando a flag está desligada: assim resumo, total e
    // payload ficam limpos sem depender de cada ponto de consumo lembrar disso.
    licenseQty (code) {
      if (!this.licensesEnabled) return 0
      return this.licenses[code] || 0
    },
    pick (user, planCode) {
      this.bulkPlan = null
      this.$emit('pick', { user, planCode })
    },
    applyAll (planCode) {
      if (!this.planByCode(planCode)) {
        this.bulkPlan = null
        return
      }
      this.bulkPlan = planCode
      this.$emit('apply-all', planCode)
    },
    bumpLicense (code, delta) {
      const next = Math.min(MAX_LICENSES_PER_PLAN, Math.max(0, this.licenseQty(code) + delta))
      this.$set(this.licenses, code, next)
      // A cobrança é de tudo que foi contratado, não só do plano das pessoas: quem
      // monta o resumo e decide se há o que pagar precisa saber a cada clique.
      this.$emit('licenses-change', Object.assign({}, this.licenses))
    },
    // Destino do "Atribuir a usuários" dos cards: só leva até a lista, sem escolher
    // plano de ninguém (decisão do revamp, TEL-2465).
    scrollToUsers () {
      const panel = this.$refs.usersPanel
      if (panel && typeof panel.scrollIntoView === 'function') {
        panel.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    },
    submit () {
      if (!this.canProceed || this.isSubmitting) return
      this.$emit('submit', {
        licenses: this.licensesEnabled ? Object.assign({}, this.licenses) : {},
        totalLicenses: this.totalLicenses,
        monthlyTotal: this.total
      })
    }
  }
}
</script>

<style lang="scss" scoped>
$green: #16a34a;
$green-d: #15803d;
$muted: #6b7280;
$border: #e5e7eb;
$amber: #b45309;
$blue: #2563eb;
$blue-d: #1d4ed8;
// Roxo do Ilimitado: o mesmo do selo do plano no resto do portal (subscription-plan-styles).
$purple: #7e22ce;

.subs-redesign {
  padding: 0 0 80px;
  color: #1f2937;
  font-family: ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, Arial, sans-serif;

  *, *:before, *:after { box-sizing: border-box; }
}

// Cabeçalho no padrão do Figma da TEL-2465, na fonte Inter (decisão do usuário,
// no lugar da Sora/Geist do design).
.sr-title {
  margin: 0 0 4px;
  font-family: 'Inter', ui-sans-serif, system-ui, sans-serif;
  font-size: 30px; line-height: 36px; font-weight: 600; letter-spacing: -0.75px; color: #0f172b;
}
.sr-sub {
  margin: 0 0 16px;
  font-family: 'Inter', ui-sans-serif, system-ui, sans-serif;
  font-size: 16px; line-height: 24px; color: #45556c;
}

.howit {
  display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
  background: #f6fbf7; border: 1px solid #e2efe6; border-radius: 12px;
  padding: 12px 16px; margin: 0 0 10px;
}
.howit-eyebrow { font-size: .7rem; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; color: $green; margin-right: 4px; }
.howit-step {
  display: inline-flex; align-items: center; gap: 7px; font-size: .86rem; font-weight: 600; color: #374151;
  svg { width: 16px; height: 16px; color: $green; flex-shrink: 0; }
}
.howit-sep { color: #cbd5e1; }
.howit-note { font-size: .82rem; color: #64748b; margin: 0 0 22px; padding: 0 2px; }

// Ciano do IA4COM — mesma cor do assistente no resto do portal.
$cyan: #36bbef;
$cyan-d: #0e7fae;
$cyan-bg: #ecf8fe;
$cyan-line: #b6e5f9;

// O recurso "IA4COM" dos planos é um link: abre a explicação.
.ia4feat {
  display: inline-flex; align-items: center; gap: 4px; padding: 0; border: 0; background: none; font: inherit;
  color: $cyan-d; font-weight: 700; cursor: pointer; text-decoration: underline dotted; text-underline-offset: 2px;
  svg { width: .8rem; height: .8rem; }
  &:hover { text-decoration-style: solid; }
  &:focus { outline: none; box-shadow: 0 0 0 3px rgba(54, 187, 239, .35); border-radius: 4px; }
  // Pelo teclado não há "passar o mouse": o foco no link mostra a mesma dica.
  // Só o foco de teclado: ao fechar a explicação o foco volta para este link, e com
  // `:focus` a dica ficava presa na tela depois de um clique.
  &:focus-visible .ia4feat-tip { opacity: 1; visibility: visible; }
}
// Dica do (i): mesmo balão do "Modelo de IA" (Pricing.vue), centrado no ícone.
.ia4feat-info {
  position: relative; display: inline-flex; align-items: center; cursor: help;
  &:hover .ia4feat-tip { opacity: 1; visibility: visible; }
}
.ia4feat-tip {
  position: absolute; bottom: calc(100% + 8px); left: 50%; transform: translateX(-50%); z-index: 60;
  width: max-content; max-width: 220px; padding: 8px 10px; border-radius: 6px;
  background: #111827; color: #f9fafb; box-shadow: 0 4px 12px rgba(0, 0, 0, .18);
  font-size: .75rem; font-weight: 400; line-height: 1.35; text-align: center; white-space: normal;
  opacity: 0; visibility: hidden; transition: opacity .15s ease; pointer-events: none;
  &::after {
    content: ''; position: absolute; top: 100%; left: 50%; margin-left: -5px;
    border: 5px solid transparent; border-top-color: #111827;
  }
}

.ia-overlay {
  position: fixed; top: 0; right: 0; bottom: 0; left: 0; z-index: 1050;
  display: flex; align-items: center; justify-content: center; padding: 16px; background: rgba(15, 23, 42, .5);
}
.ia-modal {
  position: relative; width: 100%; max-width: 560px; max-height: calc(100vh - 32px); overflow-y: auto;
  background: #fff; border: 1px solid $cyan-line; border-radius: 14px; box-shadow: 0 20px 50px rgba(0, 0, 0, .25);
}
.ia-head {
  display: flex; align-items: center; gap: 12px; padding: 16px 48px 14px 18px;
  background: $cyan-bg; border-bottom: 1px solid $cyan-line; border-radius: 14px 14px 0 0;
  h2 { margin: 0; font-size: 1.1rem; color: $cyan-d; }
  p { margin: 2px 0 0; font-size: .86rem; color: #334155; }
}
.ia-close {
  position: absolute; top: 10px; right: 10px; width: 32px; height: 32px; border: 0; border-radius: 8px;
  background: transparent; color: #475569; font-size: 1.4rem; line-height: 1; cursor: pointer;
  &:hover { background: rgba(14, 127, 174, .1); }
  &:focus { outline: none; box-shadow: 0 0 0 3px rgba(54, 187, 239, .4); }
}
.ia-body { padding: 16px 18px 18px; font-size: .88rem; color: #334155; }
.iaicon {
  width: 38px; height: 38px; border-radius: 10px; background: $cyan; color: #fff;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
  svg { width: 22px; height: 22px; }
}
.iagrid {
  list-style: none; padding: 0; margin: 0 0 10px; display: grid; gap: 6px;
  li { display: flex; gap: 7px; align-items: flex-start; }
  svg { width: 14px; height: 14px; color: $cyan-d; flex-shrink: 0; margin-top: 3px; }
}
.ialink { color: $cyan-d; font-weight: 600; font-size: .8rem; text-decoration: underline; }

.layout { display: grid; grid-template-columns: 1fr 320px; gap: 20px; align-items: start; }
.col-main { min-width: 0; }

.panel {
  border: 1px solid $border; border-radius: 12px; background: #fff; padding: 16px; margin-bottom: 20px;
  h2 { font-size: 1.1rem; margin: 0; display: flex; align-items: center; gap: 8px; }
}
.panel-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 10px; }
.head-actions { display: flex; align-items: center; gap: 8px; }
.monthsel { border: 1px solid $border; border-radius: 8px; background: #fff; padding: 7px 10px; font-size: .82rem; color: #374151; cursor: pointer; }
.resugg {
  display: inline-flex; align-items: center; gap: 6px; border: 1px solid #d1d5db; background: #fff;
  border-radius: 8px; padding: 7px 12px; font-size: .8rem; font-weight: 600; color: #475569; cursor: pointer; transition: .12s;
  svg { width: 15px; height: 15px; }
  &:hover { border-color: $green; color: $green-d; }
  &.dirty { border-color: $green; color: $green-d; background: #f0fdf4; }
}
.hint { color: $muted; font-size: .85rem; margin: 0 0 14px; }
.bulk {
  display: flex; flex-wrap: wrap; align-items: center; gap: 10px; padding: 10px;
  border: 1px solid $border; border-radius: 8px; background: #f9fafb; margin-bottom: 14px;
  .lbl { font-size: .85rem; font-weight: 600; }
}

.table-wrap { position: relative; overflow-x: auto; }
.table-loading {
  position: absolute; top: 0; left: 0; right: 0; bottom: 0; z-index: 5;
  display: flex; align-items: center; justify-content: center;
  background: rgba(255, 255, 255, .75); font-size: .85rem; color: $muted;
}
table.people {
  // Largura fixa por coluna: nome e e-mail longos passam a ser cortados com
  // reticências em vez de empurrar a tabela para fora do painel — a coluna do
  // usuário absorve o que sobra e nunca dita a largura do conjunto.
  width: 100%; table-layout: fixed; border-collapse: collapse; font-size: .9rem;
  thead tr { background: #f3f4f6; font-size: .72rem; text-transform: uppercase; letter-spacing: .03em; color: $muted; }
  th, td { padding: 10px 12px; border-bottom: 1px solid $border; vertical-align: middle; text-align: left; }
  th.c, td.c { text-align: center; }
  th.r, td.r { text-align: right; }
  th:nth-child(2), td:nth-child(2) { width: 92px; }
  th:nth-child(3), td:nth-child(3) { width: 190px; }
  th:nth-child(4), td:nth-child(4) { width: 104px; }
  tfoot tr { background: #eff6ff; font-weight: 700; border-top: 2px solid #bfdbfe; }
  tfoot td { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
}
.uname, .umail { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.uname { font-weight: 600; font-size: .88rem; }
.umail { color: $muted; font-size: .75rem; }
.fee { font-weight: 700; color: $green-d; &.empty { color: $muted; font-weight: 500; } }
.consumo { white-space: nowrap; }
// O laranja no próprio número é o aviso; o motivo vem no title, ao passar o
// mouse. Sem ícone, a coluna não muda de largura quando o alerta aparece.
.consumo.over { color: #fb923c; font-weight: 700; cursor: help; }

.planpick { display: inline-flex; align-items: center; gap: 8px; max-width: 100%; }
.psel {
  appearance: none; -webkit-appearance: none; -moz-appearance: none;
  border: 1px solid #d1d5db; border-radius: 8px; background-color: #fff;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'><path d='M1 1l4 4 4-4' stroke='%239ca3af' stroke-width='1.6' fill='none' stroke-linecap='round' stroke-linejoin='round'/></svg>");
  background-repeat: no-repeat; background-position: right 10px center;
  padding: 7px 30px 7px 11px; font-size: .85rem; font-weight: 600; cursor: pointer; min-width: 136px; max-width: 100%;
  transition: border-color .12s, box-shadow .12s;
  &:hover { border-color: #9ca3af; }
  &:focus { outline: none; border-color: $green; box-shadow: 0 0 0 3px rgba(22, 163, 74, .12); }
  option { color: #374151; font-weight: 500; }
}
.psel--none { color: #9ca3af; font-weight: 500; border-style: dashed; }
// O gêmeo com IA4COM é o mesmo plano com a IA junto: mesma cor do plano base, como
// no chip de plano do resto do portal.
.psel--manager, .psel--manager-ia4com { color: $muted; }
.psel--negotiation, .psel--negotiation-ia4com { color: $blue; }
.psel--unlimited, .psel--unlimited-ia4com { color: $purple; }

.dot { width: 9px; height: 9px; border-radius: 50%; display: inline-block; flex-shrink: 0; }
.dot--none { background: $border; box-shadow: inset 0 0 0 1px #cbd5e1; }
.dot--manager, .dot--manager-ia4com { background: #9ca3af; }
.dot--negotiation, .dot--negotiation-ia4com { background: $blue; }
.dot--unlimited, .dot--unlimited-ia4com { background: $purple; }

.panel--lic { border-color: #dbe3ea; }
.panel--lic.spotlight { border-color: #bfdbfe; box-shadow: 0 0 0 3px rgba(37, 99, 235, .08); }
.tag-opt {
  font-size: .6rem; font-weight: 700; color: #64748b; background: #f1f5f9;
  border-radius: 999px; padding: 3px 9px; letter-spacing: .03em; text-transform: uppercase;
}
.licrow {
  display: flex; align-items: center; gap: 12px; padding: 12px 4px; border-bottom: 1px solid #f1f5f9;
  &:last-of-type { border-bottom: none; }
}
.licname { flex: 1; display: flex; align-items: center; gap: 8px; font-weight: 600; font-size: .9rem; }
.licunit { color: $muted; font-weight: 500; font-size: .76rem; }
.stepper {
  display: inline-flex; align-items: center; border: 1px solid #d1d5db; border-radius: 999px; background: #fff; overflow: hidden;
  button {
    width: 32px; height: 32px; border: none; background: #fff; color: #374151;
    font-size: 1.1rem; line-height: 1; cursor: pointer; transition: background .12s;
    &:hover:not(:disabled) { background: #f3f4f6; }
    &:disabled { color: #d1d5db; cursor: not-allowed; }
  }
  .qty { min-width: 38px; text-align: center; font-weight: 700; font-size: .95rem; }
}
.licsub {
  min-width: 96px; text-align: right; font-weight: 700; color: $green-d; font-size: .9rem;
  &.empty { color: #cbd5e1; font-weight: 500; }
}
.licfoot {
  margin-top: 12px; padding-top: 12px; border-top: 1px dashed $border;
  display: flex; justify-content: space-between; font-size: .85rem;
  .v { font-weight: 700; }
}
.lichelp {
  display: flex; gap: 8px; align-items: flex-start; background: #eff6ff; border: 1px solid #bfdbfe;
  border-radius: 8px; padding: 10px 12px; font-size: .78rem; color: #1e40af; margin-bottom: 14px;
  svg { width: 16px; height: 16px; flex-shrink: 0; margin-top: 1px; }
}

.summary {
  border: 2px solid #86efac; border-radius: 12px;
  background: #f0fdf4; padding: 18px; position: sticky; top: 16px;
  h3 { margin: 0 0 10px; font-size: 1.05rem; }
}
.srow {
  padding: 8px 0; border-bottom: 1px solid #bbf7d0; font-size: .88rem;
  .l1 { display: flex; justify-content: space-between; font-weight: 600; }
  .l2 { color: #3f6212; font-size: .75rem; margin-top: 2px; .lic { color: $blue-d; font-weight: 600; } }
}
.stotal { display: flex; justify-content: space-between; padding: 10px 0 0; font-size: 1.1rem; font-weight: 700; .v { color: $green-d; } }
.due {
  background: rgba(255, 255, 255, .8); border: 1px solid #bbf7d0; border-radius: 10px;
  padding: 14px; margin: 14px 0;
  .lbl { font-size: .8rem; color: $muted; &.next { margin-top: 8px; } }
  .big { font-size: 1.4rem; font-weight: 700; }
}
.cta {
  width: 100%; background: $green; color: #fff; border: none; border-radius: 8px;
  padding: 16px; font-size: 1.05rem; font-weight: 700; cursor: pointer;
  &:hover:not(:disabled) { background: $green-d; }
  &:disabled { background: #9ca3af; cursor: not-allowed; }
}
.gate { color: $amber; font-size: .85rem; text-align: center; margin: 0 0 8px; }
.trust { color: $muted; font-size: .75rem; text-align: center; margin-top: 8px; }

@media (max-width: 860px) {
  .layout { grid-template-columns: 1fr; }
  .summary { position: static; }
}
// Celular: um plano embaixo do outro, separados por linha.
@media (max-width: 640px) {
  .plangrid, .plangrid[class*='plangrid--cols-'] { grid-template-columns: 1fr; }
  .plancell, .plancell.plancell--next-row { border-left: 0; padding: 18px 0 0; margin-top: 18px; border-top: 1px solid $border; }
  .plancell:first-child { border-top: 0; margin-top: 0; padding-top: 0; }
  // Empilhado, a vaga vazia de um gêmeo que o billing não oferta não ocupa espaço.
  .plancell.plancell--empty { display: none; }
}
</style>



// WEBPACK FOOTER //
// src/components/Subscription/PricingRedesign.vue